import { BorderRadiusOption, SpacingOption } from 'omakase/PadButton/padButtonTypes';
import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
  OmakasePolymorphicRootComponent,
} from 'omakase/utils/OmakasePolymorphicComponent';
import { Theme } from '@emotion/react';
import {
  padButtonBaseComponent,
  padButtonContainer,
  padButtonLabel,
  padButtonTypography,
} from 'omakase/PadButton/padButtonCssDefs';
import { uniqueId } from 'es-toolkit/compat';
import React from 'react';

export type PadButtonCoreType = 'radio' | 'checkbox';
export type PadButtonVariant = 'basic' | 'inverse';
export type PadButtonSelectedVariant = 'basic' | 'highContrast';

export interface PadButtonInputProps {
  /**
   * Whether or not the HTML input element is checked
   */
  checked?: boolean;
  /**
   * Whether or not the HTML input element is disabled
   */
  disabled?: boolean;
  /**
   * The onChange event for the HTML input element
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * The onBlur event for the HTML input element
   */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * The name attribute for the HTML input element
   */
  name: string;
  /**
   * The label for the pad button
   */
  label: string | React.ReactNode;
  /**
   * The type attribute for the HTML input element
   */
  type: PadButtonCoreType;
  /**
   * The value attribute for the HTML input element
   */
  value?: string;
}

export interface PadButtonBaseProps {
  /**
   * The decorative element (e.g., image, icon, etc.) to be placed inside the component
   */
  decorativeElement?: React.ReactNode;
  /**
   * Whether the unselected state shows a border
   */
  border?: boolean;
  /**
   * The design variant of the component when selected
   */
  selectedVariant?: PadButtonSelectedVariant;
  /**
   * The design variant of the component
   */
  variant: PadButtonVariant;
}

/**
 * Used internally within Omakase implemenations of PadButtonCore (e.g., PadButton, Switcher)
 */
interface PadButtonInternalProps {
  borderRadius: BorderRadiusOption;
  verticalSpacing: SpacingOption;
}

export type PadButtonCoreProps = PadButtonBaseProps & PadButtonInputProps;

type PadButtonCoreInternalProps = PadButtonCoreProps & PadButtonInternalProps;

export const PadButtonDefaultElement = 'input';

export const PadButtonCore: OmakasePolymorphicComponent<
  PadButtonCoreInternalProps,
  typeof PadButtonDefaultElement
> = React.forwardRef(
  <E extends React.ElementType = typeof PadButtonDefaultElement>(
    {
      borderRadius,
      border = false,
      checked = false,
      children,
      component,
      decorativeElement,
      disabled = false,
      onChange,
      label,
      name,
      selectedVariant = 'basic',
      verticalSpacing,
      type = 'checkbox',
      variant,
      ...rest
    }: OmakasePolymorphicComponentProps<PadButtonCoreInternalProps, E>,
    ref: React.Ref<HTMLInputElement> | undefined
  ) => {
    // inputIdRef is used to provide a unique id to link the label to the input via htmlFor.
    const inputIdRef = React.useRef(uniqueId('input'));

    const innerProps = {
      ...rest,
      css: (theme: Theme) => padButtonBaseComponent(theme, checked),
      checked,
      disabled,
      id: inputIdRef.current,
      name,
      onChange: disabled ? undefined : onChange,
      type,
    };

    const Component: React.ElementType = component || PadButtonDefaultElement;

    return (
      <div css={padButtonContainer}>
        <OmakasePolymorphicRootComponent component={Component} ref={ref} {...innerProps} />
        <label
          aria-disabled={disabled ? 'true' : undefined}
          css={(theme: Theme) =>
            padButtonLabel(
              theme,
              variant,
              verticalSpacing,
              { border, borderRadius },
              {
                checked,
                disabled,
                selectedVariant,
              }
            )
          }
          htmlFor={inputIdRef.current}
        >
          {decorativeElement}
          <span
            css={(theme: Theme) =>
              padButtonTypography(
                theme,
                {
                  checked,
                  disabled,
                  selectedVariant,
                },
                { hasDecorativeElement: !!decorativeElement }
              )
            }
          >
            {label}
          </span>
        </label>
      </div>
    );
  }
);
